/**
 * Pick products_to_offer from optional product.offerRules[] (first match),
 * else fall back to legacy product.products_to_offer.
 *
 * Keep in sync with weupsell-ai/packages/offer-resolution/resolve-effective-offer.js
 * (canonical — also re-exported from weupsell-ai/shared/resolve-effective-offer.js).
 */

function normalizeProductId(id) {
  if (id == null || id === "") return "";
  const raw = String(id);
  const gidMatch = raw.match(/Product\/(\d+)/i);
  if (gidMatch) return gidMatch[1];
  return raw.replace(/^gid:\/\/shopify\/Product\//i, "");
}

function normalizeCollectionId(id) {
  if (id == null || id === "") return "";
  if (typeof id === "object") {
    return normalizeCollectionId(id.id);
  }
  const raw = String(id).trim();
  if (!raw || raw === "[object Object]") return "";
  const gidMatch = raw.match(/Collection\/(\d+)/i);
  if (gidMatch) return gidMatch[1];
  return raw.replace(/^gid:\/\/shopify\/Collection\//i, "");
}

function collectionHandle(value) {
  if (value && typeof value === "object") {
    return String(value.handle || "")
      .trim()
      .toLowerCase();
  }
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw || raw === "[object object]") return "";
  if (/^\d+$/.test(raw) || raw.includes("collection/")) return "";
  return raw;
}

function idsEqual(a, b) {
  const left = normalizeProductId(a);
  const right = normalizeProductId(b);
  return Boolean(left) && left === right;
}

function collectionIdsEqual(a, b) {
  const left = normalizeCollectionId(a);
  const right = normalizeCollectionId(b);
  if (left && right && left === right) return true;
  const leftHandle = collectionHandle(a);
  const rightHandle = collectionHandle(b);
  return Boolean(leftHandle) && leftHandle === rightHandle;
}

function membershipSetForCollection(collectionProductIdsByKey, col) {
  if (!collectionProductIdsByKey) return null;
  const id = normalizeCollectionId(col);
  const handle = collectionHandle(col);
  const getSet = (key) => {
    if (!key) return null;
    if (typeof collectionProductIdsByKey.get === "function") {
      return collectionProductIdsByKey.get(key);
    }
    return collectionProductIdsByKey[key];
  };
  return getSet(id ? `id:${id}` : "") || getSet(handle ? `handle:${handle}` : "");
}

function productInList(productData, list) {
  if (!productData || !Array.isArray(list) || !list.length) return false;
  return list.some((entry) => {
    if (entry == null) return false;
    if (typeof entry === "object") {
      return idsEqual(entry.id, productData.id);
    }
    return idsEqual(entry, productData.id);
  });
}

function matchTextValues(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((item) =>
        typeof item === "string"
          ? item.trim()
          : String(item?.title || item?.tag || item?.vendor || item?.id || "").trim()
      )
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

function getProductCollectionRefs(productData) {
  if (!productData) return [];
  const refs = [];
  if (Array.isArray(productData.collections)) {
    productData.collections.forEach((c) => {
      if (c) refs.push(c);
    });
  }
  if (Array.isArray(productData.collection_ids)) {
    productData.collection_ids.forEach((id) => refs.push({ id }));
  }
  if (productData.collection_id != null) {
    refs.push({ id: productData.collection_id });
  }
  if (productData.collection && typeof productData.collection === "object") {
    refs.push(productData.collection);
  }
  return refs;
}

function productInCollections(productData, collections, collectionProductIdsByKey) {
  if (!productData) return false;
  const wanted = Array.isArray(collections) ? collections : [];
  if (!wanted.length) return false;

  const refs = getProductCollectionRefs(productData);
  if (
    refs.some((ref) =>
      wanted.some(
        (col) =>
          collectionIdsEqual(ref?.id ?? ref, col?.id ?? col) ||
          collectionIdsEqual(ref, col)
      )
    )
  ) {
    return true;
  }

  const pid = normalizeProductId(productData.id);
  if (!pid || !collectionProductIdsByKey) return false;

  return wanted.some((col) => {
    const set = membershipSetForCollection(collectionProductIdsByKey, col);
    return Boolean(set && typeof set.has === "function" && set.has(pid));
  });
}

export function matchOfferWhen(when, productData, options = {}) {
  if (!when || !when.type) return false;
  const type = when.type;
  const value = when.value;
  const collectionProductIdsByKey =
    options.collectionProductIdsByKey || null;

  switch (type) {
    case "all_product":
      return true;
    case "all_except":
      return Boolean(productData) && !productInList(productData, value);
    case "specific_product":
      return productInList(productData, value);
    case "product_from_collection":
      return productInCollections(productData, value, collectionProductIdsByKey);
    case "product_specific_title": {
      if (!productData?.title) return false;
      const titles = matchTextValues(value);
      return titles.some((val) => productData.title.includes(val));
    }
    case "product_specific_tag": {
      if (!Array.isArray(productData?.tags) || !productData.tags.length) {
        return false;
      }
      const needles = matchTextValues(value).map((t) => t.toLowerCase());
      if (!needles.length) return false;
      const productTags = productData.tags.map((tag) =>
        String(tag || "")
          .trim()
          .toLowerCase()
      );
      return needles.some((needle) => productTags.includes(needle));
    }
    case "product_specific_vendor": {
      if (!productData?.vendor) return false;
      const vendors = matchTextValues(value).map((v) => v.toLowerCase());
      if (!vendors.length) return false;
      return vendors.includes(String(productData.vendor).trim().toLowerCase());
    }
    default:
      return false;
  }
}

export function matchOfferWhenAny(when, products, options = {}) {
  const list = Array.isArray(products)
    ? products.filter(Boolean)
    : products
      ? [products]
      : [];
  if (!list.length) {
    return when?.type === "all_product";
  }
  return list.some((product) => matchOfferWhen(when, product, options));
}

export function isDefaultOfferEnabled(productCond) {
  return productCond?.defaultOfferEnabled !== false;
}

export function resolveEffectiveOffer(
  productCond,
  contextProductOrProducts,
  options = {}
) {
  const fallbackOffer = productCond?.products_to_offer || {
    type: "shopify_recommended",
    value: [],
  };
  const fallbackLimit = Math.max(
    1,
    Number(productCond?.products_to_offer_limit) || 1
  );

  const rules = Array.isArray(productCond?.offerRules)
    ? productCond.offerRules
    : [];

  const contextProducts = Array.isArray(contextProductOrProducts)
    ? contextProductOrProducts
    : contextProductOrProducts
      ? [contextProductOrProducts]
      : [];

  for (let i = 0; i < rules.length; i += 1) {
    const rule = rules[i];
    if (!rule || rule.enabled === false) continue;
    const when = rule.when;
    if (!when?.type) continue;
    if (!matchOfferWhenAny(when, contextProducts, options)) continue;

    const offer = rule.products_to_offer;
    if (!offer?.type) continue;

    const limit = Math.max(
      1,
      Number(rule.products_to_offer_limit) ||
        Number(productCond?.products_to_offer_limit) ||
        1
    );

    return {
      products_to_offer: offer,
      products_to_offer_limit: limit,
      matchedRuleId: rule.id != null ? String(rule.id) : `rule_${i}`,
      source: "rule",
    };
  }

  if (!isDefaultOfferEnabled(productCond)) {
    return {
      products_to_offer: null,
      products_to_offer_limit: 0,
      matchedRuleId: null,
      source: "none",
    };
  }

  return {
    products_to_offer: fallbackOffer,
    products_to_offer_limit: fallbackLimit,
    matchedRuleId: null,
    source: "fallback",
  };
}

export function createEmptyOfferRule(overrides = {}) {
  const id =
    overrides.id ||
    `rule_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  return {
    id,
    enabled: true,
    when: {
      type: "specific_product",
      value: [],
    },
    products_to_offer: {
      type: "specific_product",
      value: [],
    },
    products_to_offer_limit: 1,
    ...overrides,
  };
}

export function buildOfferRuleContextProducts(context = {}) {
  const products = [];
  const seen = new Set();

  const pushProduct = (raw) => {
    if (!raw || typeof raw !== "object") return;
    const id = normalizeProductId(raw.id || raw.productId);
    if (!id || seen.has(id)) return;
    seen.add(id);
    products.push({
      id,
      title: String(raw.title || raw.untranslatedTitle || "").trim(),
      vendor: String(raw.vendor || "").trim(),
      productType: String(raw.productType || raw.product_type || "").trim(),
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      collections: Array.isArray(raw.collections) ? raw.collections : [],
    });
  };

  for (const cartLine of context.cartLines || []) {
    const merchandise = cartLine?.merchandise;
    if (merchandise?.product) {
      pushProduct(merchandise.product);
    } else if (merchandise?.productId) {
      pushProduct({ id: merchandise.productId });
    }

    for (const component of cartLine?.lineComponents || []) {
      const nested = component?.merchandise;
      if (nested?.product) {
        pushProduct(nested.product);
      } else if (nested?.productId) {
        pushProduct({ id: nested.productId });
      }
    }
  }

  for (const item of context.cartItems || context.items || []) {
    if (item?.product) {
      pushProduct(item.product);
    } else {
      pushProduct({
        id: item?.product_id || item?.productId || item?.id,
        title: item?.product_title || item?.title,
        vendor: item?.vendor,
        tags: item?.tags,
      });
    }
  }

  for (const orderLine of context.lineItems || []) {
    if (orderLine?.product) {
      pushProduct(orderLine.product);
    } else {
      pushProduct({
        id: orderLine?.productId || orderLine?.product?.id,
        title: orderLine?.title || orderLine?.product?.title,
        vendor: orderLine?.vendor || orderLine?.product?.vendor,
      });
    }
  }

  if (context.productDetails) {
    pushProduct(context.productDetails);
  } else if (context.productId) {
    pushProduct({ id: context.productId });
  }

  return products;
}
